/*
 *
 * Copyright (c) Vaisala Oyj. All rights reserved.
 *
 */

import * as React from 'react';
import { BodyText, Icon, Spinner} from '@vaisala/rockhopper-components';
import { Progress } from './Progress';
import { useProject } from '../state/project';
import { DataStatus, PageProcessStatus } from '../pages/DataManagementPage';
import { VaiIcon } from '@vaisala/rockhopper-design-tokens';
import styled from 'styled-components';
import { useAuth } from '../state/auth/AuthContext';

const UploadStatusContainer = styled.div`
    height : 100%;
    width : 100%;
    display : flex;
    align-items : end;
    bottom: 0px;
`;

interface Props {
    processStatus : PageProcessStatus;
    newStatus : DataStatus;
}

export const UploadStatus  : React.FC<Props> = (props) => { 

    const { processStatus, newStatus } = props;

    const { project, finderStatus, setFinderStatus, uploadInfo, setUploadInfo } = useProject();

    const progressLoop = React.useRef<ReturnType<typeof setInterval>>();

    const [ progressInfo, setProgressInfo ] = React.useState({
        main_label : "Upload ",
        main_progress : 0,
        secondary_label : "",
        secondary_progress : 0,
        current_file : ""
    });

    const [ finderProgressInfo, setFinderProgressInfo ] = React.useState({
        main_label : "Finder",
        repo : "",
        file : ""
    });

    React.useEffect(() => {

        if(uploadInfo.status === 'RUNNING' && uploadInfo.project === project.name){
            clearInterval(progressLoop.current);
            progressLoop.current = setInterval(fetchProgress, 1500);
        } else if(uploadInfo.status === 'ERROR' && uploadInfo.project === project.name){
            clearInterval(progressLoop.current);
        } else if(uploadInfo.status === 'FINISHED' && uploadInfo.project === project.name){
            clearInterval(progressLoop.current);
        } else if (uploadInfo.status === "STOP" && uploadInfo.project === project.name) {
            clearInterval(progressLoop.current);
            setProgressInfo({
                main_label : "Upload ",
                main_progress : 0,
                secondary_label : "",
                secondary_progress : 0,
                current_file : ""
            })
        } 

        return () => {
            clearInterval(progressLoop.current);
        }

    }, [uploadInfo, finderStatus]);

    const {secureFetch} = useAuth()

    const fetchProgress = async () => {
        try {
            const response = await secureFetch(`/api/upload/status`);
            const data = await response.json();
    
            const new_status : string = data.status;
            const new_secondary_label : string = data.day;
            const new_current_file : string = data.current_file;
            const new_main_progress : number = (data.total_files===0)?0:(data.total_counter/data.total_files);
            const new_secondary_pogress : number = (data.day_files===0)?0:(data.day_counter/data.day_files);
    
            setProgressInfo({
                main_label : "Upload",
                main_progress : new_main_progress,
                secondary_label : new_secondary_label,
                secondary_progress : new_secondary_pogress,
                current_file : new_current_file
            });
            if (uploadInfo.status !== "STOP"){
                if (new_status === "crashed") {
                    setUploadInfo({...uploadInfo, status : 'ERROR'});
                    console.log('Upload process crashed');
                }
                else if ( new_status === "finished" ){
                    console.log('Upload process finished');
                    setUploadInfo({...uploadInfo, status : "FINISHED", n_files_uploaded : data.total_counter});
                }
                else if ( new_status === "stop_cmd" ){
                    console.log('Upload process stopped by user');
                    setUploadInfo({...uploadInfo, status : "STOP", n_files_uploaded : data.total_counter});
                }                  
            }
        } catch (error) {
            console.error('Error fetching progress:', error);
        }
    };

    React.useEffect(() => {

        if(finderStatus === "RUNNING"){
            clearInterval(progressLoop.current);
            progressLoop.current = setInterval(fetchFinderProgress, 1000);
        } else if (finderStatus === "LOADING") {
            setFinderProgressInfo({
                main_label : "Finder loading new files...",
                repo : "",
                file : ""
            })
        } else if (finderStatus === "STOP") {
            clearInterval(progressLoop.current);
            setFinderProgressInfo({
                main_label : "Stopping finder process... Wait a few seconds",
                repo : "",
                file : ""
            })
        } else if (finderStatus === "ERROR" || finderStatus === "FINISHED") {
            clearInterval(progressLoop.current);
        }

        return () => {
            clearInterval(progressLoop.current);
        }

    }, [finderStatus]);

    const fetchFinderProgress = async () => {
        try {
            const response = await secureFetch(`/api/projects/${project.name}/finder_status`);
            const data = await response.json();

            const new_status : string = data.status;
            const new_repo : string = data.repo;
            const new_file : string = data.file;
            if (finderStatus !== 'STOP'){
                if (new_status === "crashed") {
                    setFinderStatus("ERROR");
                    setFinderProgressInfo({
                        main_label : "Finder interrupted unexpectedly",
                        repo : "",
                        file : ""
                    });
                }
                else if ( new_status === "finished" ){
                    setFinderStatus("FINISHED");
                    setFinderProgressInfo({
                        main_label : "Finder",
                        repo : "",
                        file : ""
                    });
                }
                else if ( new_status === "scanning"){
                    setFinderProgressInfo({
                        main_label : "Finder scanning ",
                        repo : new_repo,
                        file : new_file
                    });
                }
                else if ( new_status === "filtering"){
                    setFinderProgressInfo({
                        main_label : "Finder filtering ",
                        repo : new_repo,
                        file : new_file
                    });
                }
                else if ( new_status === "parsing"){
                    setFinderProgressInfo({
                        main_label : "Finder parsing ",
                        repo : new_repo,
                        file : new_file
                    });
                    setTimeout(fetchFinderProgress, 1000);
                }
                else if ( new_status === "checking"){
                    setFinderProgressInfo({
                        main_label : "Finder checking ",
                        repo : new_repo,
                        file : new_file
                    });
                }                
            }

        } catch (error) {
            console.error('Error fetching progress of search:', error);
        }
    };

    const renderUploadStatusBox = () => {

        if (processStatus === "ON_DATA_FETCHING"){

            if (finderStatus === "STOP"){
                return (
                    <div>
                        <Spinner />
                        <BodyText>
                        <b>{finderProgressInfo.main_label + " " + finderProgressInfo.repo + " " + finderProgressInfo.file}</b>.
                        </BodyText>
                    </div>
                );
            } else {
                return (
                    <div>
                        <BodyText>
                        Currently looking for <b>new files</b>.
                        </BodyText>
                        <BodyText>
                        <b>{finderProgressInfo.main_label + " " + finderProgressInfo.repo + " " + finderProgressInfo.file}</b>.
                        </BodyText>
                    </div>
                );                
            }
        } else {
            if (uploadInfo.status === 'ERROR') {
                return (
                    <div>
                        <BodyText>
                        <b>Error</b> while uploading <b>{uploadInfo.n_files}</b> files.
                        </BodyText>
                        <BodyText>
                        <b>{uploadInfo.dates.length}</b> day(s) starting from <b>{uploadInfo.dates[0]}</b> until <b>{uploadInfo.dates[uploadInfo.dates.length-1]}</b>.
                        </BodyText>
                    </div>
                );
            } else if (uploadInfo.status === "STOP"){
                return (
                    <div>
                        <Spinner />
                        <BodyText>
                            Stopping upload process... This can take a few seconds... Please wait.
                        </BodyText>
                    </div>
                );
            } else if (uploadInfo.status !== 'RUNNING' && (uploadInfo.project !== "" && uploadInfo.project !== project.name)){
                return (            
                    <div>
                    <BodyText>
                    <b>No data</b> ready to be download.
                    </BodyText>          
                    <BodyText>
                    Search new files to upload using "find new files".
                    </BodyText>
                    </div>
                );
            }
            else if (uploadInfo.status !== 'RUNNING' && newStatus === 'NO_DATA') {
                return (            
                    <div>
                    <BodyText>
                    <b>No data</b> ready to be download.
                    </BodyText>          
                    <BodyText>
                    Search new files to upload using "find new files".
                    </BodyText>
                    </div>
                );
            }
            else if (uploadInfo.status !== 'RUNNING' && newStatus === 'DATA') {
                return (            
                    <div>
                    <BodyText>
                    <b>{uploadInfo.n_files}</b> files ready to be uploaded.
                    </BodyText>  
                    {uploadInfo.dates.length === 0 ?        
                    <BodyText>
                    <b>No date</b> has been currently selected
                    </BodyText> :
                    <BodyText>
                    <b>{uploadInfo.dates.length}</b> day(s) starting from <b>{uploadInfo.dates[0]}</b> until <b>{uploadInfo.dates[uploadInfo.dates.length-1]}</b>.
                    </BodyText>
                    }
                    </div>
                );
            }
            else if (uploadInfo.status === 'FINISHED' && newStatus === 'DATA_UPLOADED') {
                return (            
                    <div>
                        <BodyText>
                        <b>{uploadInfo.n_files_uploaded + "/" + uploadInfo.n_files}</b> successfully uploaded.
                        </BodyText>
                        <BodyText>
                        <b>{uploadInfo.dates.length}</b> day(s) starting from <b>{uploadInfo.dates[0]}</b> until <b>{uploadInfo.dates[uploadInfo.dates.length-1]}</b>.
                        </BodyText>
                    </div>
                );
            }
            else if (uploadInfo.status === 'NONE' && newStatus === 'DATA_UPLOADED') {
                return (            
                    <div>
                        <BodyText>
                        Upload process interrupted.
                        </BodyText>
                        <BodyText>
                            <b>No files</b> have been uploaded.
                        </BodyText>
                    </div>
                );
            }
            else if (uploadInfo.status === 'RUNNING') {
                return (
                    <div>
                        <Icon name={VaiIcon.Warning}/>
                        <BodyText>
                        <b>Upload already running</b> for project <b>{uploadInfo.project}</b>.
                        </BodyText>
                    </div>
                );
            }
            
            return (<div></div>);            
        }

    };

    return (
        <UploadStatusContainer>
            {
                (uploadInfo.status === 'RUNNING') && (uploadInfo.project === project.name) ?
                <Progress
                main_label={progressInfo.main_label}
                main_progress={progressInfo.main_progress}
                secondary_label={progressInfo.secondary_label}
                secondary_progress={progressInfo.secondary_progress}
                increment={progressInfo.current_file}
                /> :
                renderUploadStatusBox()
            }
        </UploadStatusContainer>         
    );
};